import { service } from '@maia/cart';
import { IconAngleRight } from '@maia/icons';
import { Button, PageHeader } from '@maia/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getCancellationQuestionsOptions } from '../../../../features/cancel-insurance/get-questions/queryOptions';
import { Form } from '../../../../features/form/base-form/base-form';
import { mapToAnswer } from '../../../../features/form/utils/map-to-answers';
import { useCallback, useMemo } from 'react';
import { flow } from 'es-toolkit';
import { match } from 'ts-pattern';
import { DocumentDto } from '@maia/shared';
import * as v from 'valibot';
import { formatDate } from '@maia/utils';

export const Route = createFileRoute(
  '/_authenticated/cancel-insurance/$customer/cart/$cart/insurance/$insurance/_layout/',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer, cart, insurance },
  }) =>
    queryClient.ensureQueryData(
      getCancellationQuestionsOptions({
        cancellationGuid: cart,
        insuranceGuid: insurance,
        customerGuid: customer,
      }),
    ),
});

function RouteComponent() {
  const { cart, customer, insurance } = Route.useParams();
  const cancellation = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const { data, refetch } = useQuery(
    getCancellationQuestionsOptions({
      cancellationGuid: cart,
      insuranceGuid: insurance,
      customerGuid: customer,
    }),
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (body: {
      questions: Parameters<
        typeof service.putCancellationQuestions
      >[number]['questions'];
      trigger: 'revalidate' | 'submit';
    }) =>
      service.putCancellationQuestions({
        cancellationGuid: cart,
        customerGuid: customer,
        questions: body.questions,
      }),
    onSuccess: (_, request) =>
      match(request.trigger)
        .with('revalidate', async () => {
          await refetch();
        })
        .with('submit', async () => {
          await refetch();
          navigate({
            to: '/cancel-insurance/$customer/cart/$cart/insurance/$insurance/summary',
            params: { customer, cart },
          });
        })
        .exhaustive(),
  });

  const defaultValues = useMemo(
    () =>
      data
        ? data.questions.map((question) => {
            if (question.reference === 'cancellation_effective_date') {
              return {
                ...question,
                answer: {
                  ...question.answer,
                  value:
                    typeof question.answer.value === 'string'
                      ? new Date(question.answer.value)
                      : new Date(),
                },
              };
            }
            return question;
          })
        : [],
    [data?.questions, data?.minimumCancellationDate],
  );

  const handleRevalidate = useCallback(
    flow(mapToAnswer, (questions) => {
      const filteredQuestions = questions.filter(
        (q) => !(q.answer instanceof DocumentDto),
      );

      return mutateAsync({
        questions: filteredQuestions,
        trigger: 'revalidate',
      });
    }),
    [mutateAsync],
  );

  const handleSubmit = useCallback(
    flow(mapToAnswer, (questions) => {
      const filteredQuestions = questions.filter(
        (q) => !(q.answer instanceof DocumentDto),
      );
      return mutateAsync({
        questions: filteredQuestions,
        trigger: 'submit',
      });
    }),
    [mutateAsync],
  );

  const schemaFields: Record<string, any> = {};

  for (const question of defaultValues) {
    if (
      question.reference === 'cancellation_effective_date' &&
      cancellation.minimumCancellationDate &&
      cancellation.maximumCancellationDate
    ) {
      schemaFields[question.guid] = v.pipe(
        v.date(),
        v.minValue(
          cancellation.minimumCancellationDate,
          `De datum moet na ${formatDate(cancellation.minimumCancellationDate)} liggen`,
        ),
        v.maxValue(
          cancellation.maximumCancellationDate,

          `De datum moet voor ${formatDate(cancellation.maximumCancellationDate)} liggen`,
        ),
      );
    }
  }

  return (
    <>
      <PageHeader
        backLink={{ to: '/insurances/$guid', params: { guid: insurance } }}
        title="Verzekering opzeggen"
        className="area-page-header"
      />

      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <Form
            questions={defaultValues}
            className="flex flex-col gap-y-32"
            submitButtonSlot={
              <Button
                type="submit"
                iconRight={IconAngleRight}
                className="ml-auto w-fit"
                isLoading={isPending}
                isDisabled={isPending}
              >
                Volgende
              </Button>
            }
            onSubmit={handleSubmit}
            onRevalidate={handleRevalidate}
            validationSchema={v.object(schemaFields)}
          />
        </div>
      </div>
    </>
  );
}
